import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';

/**
 * Custom hook to detect and manage new task assignment notifications
 * Tracks which tasks user has been notified about to distinguish new vs existing
 * Returns notification state and dismiss functionality
 */
export const useNewTaskNotifications = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [newTaskNotification, setNewTaskNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  // Storage key for tracking notified tasks
  const getStorageKey = () => `crisiscompass_notified_tasks_${currentUser?.uid}`;

  // Get previously notified task IDs from localStorage
  const getNotifiedTaskIds = useCallback(() => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.error('Error reading notified tasks from localStorage:', error);
      return new Set();
    }
  }, [currentUser?.uid]);

  // Save notified task IDs to localStorage
  const saveNotifiedTaskIds = useCallback((taskIds) => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify([...taskIds]));
    } catch (error) {
      console.error('Error saving notified tasks to localStorage:', error);
    }
  }, [currentUser?.uid]);

  // Dismiss current notification
  const dismissNotification = useCallback(() => {
    if (newTaskNotification) {
      // Mark these tasks as notified
      const notifiedIds = getNotifiedTaskIds();
      newTaskNotification.tasks.forEach(task => notifiedIds.add(task.id));
      saveNotifiedTaskIds(notifiedIds);
      
      console.log('🔔 Dismissed notification for tasks:', newTaskNotification.tasks.map(t => t.name));
      setNewTaskNotification(null);
    }
  }, [newTaskNotification, getNotifiedTaskIds, saveNotifiedTaskIds]);

  // Clear all notification history (for testing/reset)
  const clearNotificationHistory = useCallback(() => {
    localStorage.removeItem(getStorageKey());
    setNewTaskNotification(null);
    console.log('🔔 Cleared all task notification history');
  }, [getStorageKey]);

  // Show all outstanding tasks on login
  const showAllOutstandingTasks = useCallback(() => {
    console.log('🔔 showAllOutstandingTasks: Triggering display of all outstanding tasks on login');
    // Clear the notified tasks history so all outstanding tasks appear as "new"
    localStorage.removeItem(getStorageKey());
    // The effect will re-run and show all outstanding tasks
  }, [getStorageKey]);

  useEffect(() => {
    if (!currentUser?.orgId || !currentUser?.role || !db) {
      setNewTaskNotification(null);
      setLoading(false);
      return;
    }

    console.log('🔔 useNewTaskNotifications: Setting up listener for new task assignments');

    const tasksRef = collection(db, 'c3_tasks');
    const q = query(
      tasksRef,
      where('orgId', '==', currentUser.orgId),
      where('assignedRole', '==', currentUser.role),
      where('status', '!=', 'complete')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const allTasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('🔔 useNewTaskNotifications: Processing tasks for new assignments:', allTasks.length);

        // Filter tasks to only include those from active incidents
        const validTasks = [];
        for (const task of allTasks) {
          if (task.incidentId) {
            try {
              const incidentRef = doc(db, 'c3_incidents', task.incidentId);
              const incidentDoc = await getDoc(incidentRef);
              
              if (incidentDoc.exists()) {
                const incident = incidentDoc.data();
                // Only include tasks from active, stabilising, or exercise incidents
                if (incident.status === 'Active' || incident.status === 'Stabilising' || incident.status === 'Exercise') {
                  validTasks.push(task);
                }
              } else {
                // 🧹 CLEANUP: Skip orphaned tasks with missing incidents (reduces log noise)
                console.log(`🧹 Skipping orphaned task ${task.id} - incident ${task.incidentId} no longer exists`);
                // Skip this task instead of including it
                continue;
              }
            } catch (error) {
              console.warn('🔔 Error checking incident status for task:', task.id, error);
              console.warn('🔔 Task details:', { 
                taskId: task.id, 
                incidentId: task.incidentId, 
                taskOrgId: task.orgId, 
                userOrgId: currentUser?.orgId,
                userRole: currentUser?.role
              });
              // Include task anyway if we can't check incident - permissions issue
              validTasks.push(task);
            }
          } else {
            // Tasks without incident ID are probably orphaned, include them
            validTasks.push(task);
          }
        }

        console.log('🔔 useNewTaskNotifications: Valid tasks from active incidents:', validTasks.length);

        // Get previously notified task IDs
        const notifiedIds = getNotifiedTaskIds();
        
        // Find truly NEW tasks (not in notified set)
        const newTasks = validTasks.filter(task => !notifiedIds.has(task.id));
        
        if (newTasks.length > 0) {
          console.log('🔔 useNewTaskNotifications: Found new task assignments:', newTasks.map(t => t.name));
          
          // Create notification object
          setNewTaskNotification({
            id: `notification_${Date.now()}`,
            tasks: newTasks,
            timestamp: new Date(),
            count: newTasks.length
          });
        } else {
          // No new tasks, but update notified list with any existing valid tasks
          // This prevents re-notifications on page refresh
          const allTaskIds = new Set([...notifiedIds]);
          validTasks.forEach(task => allTaskIds.add(task.id));
          
          if (allTaskIds.size > notifiedIds.size) {
            saveNotifiedTaskIds(allTaskIds);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('❌ Error processing task notifications:', error);
        setLoading(false);
      }
    }, (error) => {
      console.error('❌ Error in new task notifications listener:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.orgId, currentUser?.role, db, getNotifiedTaskIds, saveNotifiedTaskIds]);

  return {
    newTaskNotification,
    dismissNotification,
    clearNotificationHistory,
    showAllOutstandingTasks,
    loading
  };
};